import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import {
  Observable,
  BehaviorSubject,
  throwError,
  EMPTY,
} from 'rxjs';
import {
  catchError,
  filter,
  switchMap,
  take,
  finalize,
} from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { ApiConstants } from '../constants/api.constants';

/**
 * TokenRefreshInterceptor
 *
 * Handles the full 401 → refresh → retry flow:
 *
 *  1. Any protected API returns 401 (expired access token).
 *  2. This interceptor detects it and calls POST /auth/refresh ONCE.
 *  3. All other concurrent 401s queue on newToken$ and wait.
 *  4. On refresh success: stores new tokens, retries all queued requests.
 *  5. On refresh failure: clears session, redirects to /login — no toast.
 *  6. /auth/login, /auth/refresh, /auth/logout are never intercepted
 *     to prevent infinite loops.
 */
@Injectable()
export class TokenRefreshInterceptor implements HttpInterceptor {

  /** True while a refresh call is in flight. Prevents parallel refresh calls. */
  private refreshing = false;

  /**
   * null  → refresh is in progress (queued requests filter and wait).
   * token → refresh succeeded; queued requests take(1) and replay.
   */
  private newToken$ = new BehaviorSubject<string | null>(null);

  /** Auth endpoints that must never trigger a refresh attempt. */
  private readonly skipUrls = [
    ApiConstants.AUTH_LOGIN,
    ApiConstants.AUTH_REFRESH,
    ApiConstants.AUTH_LOGOUT,
  ];

  constructor(private authService: AuthService) { }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {

        // Pass through anything that is not a 401, or is on a skip URL
        if (error.status !== 401 || this.isSkippedUrl(req.url)) {
          return throwError(() => error);
        }

        // Session was force-terminated (force-logout user/all). The access AND
        // refresh tokens are invalid, so a refresh would just fail — skip it and
        // sign the user out immediately with a reason.
        if (error.headers?.get('X-Session-Terminated') === 'true') {
          this.authService.clearSessionAndRedirect('Your session was ended by an administrator. Please sign in again.');
          return EMPTY;
        }

        // A refresh is already running → queue this request
        if (this.refreshing) {
          return this.waitForNewTokenAndRetry(req, next);
        }

        // Kick off the one-and-only refresh call.
        // Even if there is no stored refresh token we let the server decide —
        // the refresh call will fail and clearSessionAndRedirect() handles it.
        return this.startRefreshAndRetry(req, next);
      })
    );
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  private isSkippedUrl(url: string): boolean {
    return this.skipUrls.some(path =>
      url.toLowerCase().includes(path.toLowerCase())
    );
  }

  /**
   * Executes POST /auth/refresh once.
   * On success → stores new tokens, emits to newToken$, retries the original request.
   * On failure → clears session and redirects to /login.
   */
  private startRefreshAndRetry(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {

    this.refreshing = true;
    this.newToken$.next(null); // signal "refresh in progress" to any queued requests

    return this.authService.refreshAccessToken().pipe(

      switchMap(response => {
        if (!response.success || !response.data?.accessToken) {
          // Server returned 200 but success=false — treat as failure
          throw new Error('Refresh response unsuccessful');
        }

        const newAccessToken = response.data.accessToken;

        // Unblock all queued requests with the new token
        this.newToken$.next(newAccessToken);

        // Retry the original request with the new token
        return next.handle(this.cloneWithToken(req, newAccessToken));
      }),

      catchError(() => {
        // Refresh call failed (400 / 401 / network error)
        // Reset subject so any late-arriving queued requests don't hang
        this.newToken$.next(null);
        this.authService.clearSessionAndRedirect();
        return EMPTY; // suppress error — no toast, just redirect
      }),

      finalize(() => {
        this.refreshing = false;
      })
    );
  }

  /**
   * A concurrent request received 401 while a refresh was already in flight.
   * Subscribe to newToken$, skip null emissions (refresh still running),
   * and replay the request the moment the new token arrives.
   */
  private waitForNewTokenAndRetry(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return this.newToken$.pipe(
      filter((token): token is string => token !== null),
      take(1),
      switchMap(token => next.handle(this.cloneWithToken(req, token)))
    );
  }

  /** Returns a cloned request with a fresh Authorization header. */
  private cloneWithToken(
    req: HttpRequest<any>,
    token: string
  ): HttpRequest<any> {
    return req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }
}
